import { useTranslation } from 'react-i18next';
import {
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
} from '@monorepo/nova-ui';
import {
  CreditAction,
  CreditTransaction as CreditTransactionType,
  CreditType,
} from '@monorepo/types';
import { useUserCreditHistory } from '../../api/queries/user-credit-history-queries';
import { formatAmount, formatDate, getAmountColor } from './balance-utils';

type PrizeNoteData = {
  prizeId?: string;
  contestName?: string;
  rank?: number;
};

const parsePrizeNoteData = (notes?: string): PrizeNoteData | null => {
  if (!notes) {
    return null;
  }

  try {
    const parsedValue: unknown = JSON.parse(notes);

    if (!parsedValue || typeof parsedValue !== 'object') {
      return null;
    }

    const parsedNote = parsedValue as Record<string, unknown>;
    const prizeId =
      typeof parsedNote.prizeId === 'string' ? parsedNote.prizeId : undefined;
    const contestName =
      typeof parsedNote.contestName === 'string'
        ? parsedNote.contestName
        : undefined;
    const rank =
      typeof parsedNote.rank === 'number' ? parsedNote.rank : undefined;

    if (!prizeId && !contestName && typeof rank !== 'number') {
      return null;
    }

    return { prizeId, contestName, rank };
  } catch {
    return null;
  }
};

const formatOrdinal = (value: number): string => {
  const absValue = Math.abs(value);
  const mod100 = absValue % 100;

  if (mod100 >= 11 && mod100 <= 13) {
    return `${value}th`;
  }

  const mod10 = absValue % 10;

  if (mod10 === 1) {
    return `${value}st`;
  }

  if (mod10 === 2) {
    return `${value}nd`;
  }

  if (mod10 === 3) {
    return `${value}rd`;
  }

  return `${value}th`;
};

const BalanceTransactions = ({ userId }: { userId?: string } = {}) => {
  const { data, isPending } = useUserCreditHistory(userId);
  const transactions = data?.transactions;
  const { palette, typography } = useTheme();
  const { t } = useTranslation('hubCommon');

  const getCommentValue = (transaction: CreditTransactionType): string => {
    if (transaction.type === CreditType.Prize) {
      const parsedPrizeNote = parsePrizeNoteData(transaction.notes);

      if (
        parsedPrizeNote?.contestName &&
        typeof parsedPrizeNote.rank === 'number'
      ) {
        return t('prizeFromContest', {
          rank: formatOrdinal(parsedPrizeNote.rank),
          contestName: parsedPrizeNote.contestName,
        });
      }

      if (parsedPrizeNote?.prizeId) {
        return t('contestPrize');
      }
    }

    return (
      transaction.notes ||
      (transaction.purchaseId
        ? `${t('orderId')}: ${transaction.purchaseId}`
        : '')
    );
  };

  if (isPending)
    return <Skeleton variant="rectangular" width="100%" height={120} />;

  if (!transactions) return null;

  return (
    <TableContainer
      sx={{
        paddingInline: typography.pxToRem(16),
        borderRadius: typography.pxToRem(16),
        border: `${typography.pxToRem(1)} solid ${palette.text.fifth}`,
        overflowX: 'auto',
      }}
    >
      <Table
        sx={{
          width: '100%',
          minWidth: { xs: 600, sm: '100%' },
          tableLayout: { xs: 'auto', sm: 'fixed' },
          '& .MuiTableCell-root': {
            whiteSpace: 'nowrap',
          },
          '& .MuiTableHead-root .MuiTableRow-root .MuiTableCell-root': {
            borderBottom: `${typography.pxToRem(1)} solid ${palette.divider}`,
          },
        }}
        aria-label="balance transactions table"
      >
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: '25%' }}>{t('action')}</TableCell>
            <TableCell sx={{ width: '25%' }}>{t('amount')}</TableCell>
            <TableCell sx={{ width: '25%' }}>{t('created')}</TableCell>
            <TableCell sx={{ width: '25%' }}>{t('comment')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {transactions.map((transaction) => (
            <TableRow key={transaction._id}>
              <TableCell component="th" scope="row" sx={{ fontWeight: 500 }}>
                {transaction.action === CreditAction.Add
                  ? t('added')
                  : t('deducted')}
              </TableCell>
              <TableCell
                sx={{ color: getAmountColor(transaction.action, palette) }}
              >
                {formatAmount(transaction.amount, transaction.action)}
              </TableCell>
              <TableCell>
                {formatDate(new Date(transaction.createdAt))}
              </TableCell>
              <TableCell>{getCommentValue(transaction)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default BalanceTransactions;
